/**
 * Acknowledgement, compaction and tombstones.
 *
 * WHY THIS IS A RULE AND NOT A CRON JOB
 * ------------------------------------
 * Because the dangerous mistake here is deleting a change some device still
 * needs. A device offline for three weeks has a cursor far behind the log, and a
 * cleanup that keeps only "the last N changes" will happily delete the very
 * changes that device was going to download — without an error, so the device
 * returns with a database missing three weeks of invoices.
 *
 * So compaction is a DERIVED quantity, not a policy a caller picks:
 *
 *   compact only above  min(acknowledged cursor of every ACTIVE device)
 *
 * One slow device pins the log. That is correct. A log that grows is a storage
 * problem with a known fix (that device syncs, or is removed); data that cannot
 * be recovered is not a trade-off worth making.
 */

/** One device's position, as the cloud knows it. */
export interface DeviceAcknowledgement {
  readonly businessId: string;
  readonly deviceId: string;
  /** How far this device has applied. */
  readonly cursor: string;
  /** When it last reported. Decides whether it is still ACTIVE. */
  readonly lastSeenAt: string;
  readonly deviceLabel?: string;
}

/** Compaction inputs. Everything the rule needs, and nothing it does not. */
export interface CompactionInput {
  /** One entry per device the cloud knows about. */
  readonly acknowledgements: readonly DeviceAcknowledgement[];
  /** The oldest change still in the log. */
  readonly oldestRetainedCursor: string;
  /**
   * A device unseen for longer than this stops holding the log hostage.
   *
   * Not a "discard its data" switch. It means the device is assumed to recover
   * from a snapshot instead — a RECOVERY path, not data loss, because the
   * snapshot still holds its records.
   */
  readonly staleAfterMs: number;
  readonly now: string;
}

export interface CompactionDecision {
  /** Everything below this cursor can go. */
  readonly compactBelow: string;
  /** Devices ignored as stale, for the record. */
  readonly ignoredStaleDeviceIds: readonly string[];
}

/** How long a device may be silent before it stops pinning the log. */
export const DEFAULT_STALE_AFTER_MS = 90 * 24 * 60 * 60 * 1000;

export function planCompaction(input: CompactionInput): CompactionDecision {
  const cutoff = new Date(input.now).getTime() - input.staleAfterMs;
  const active: string[] = [];
  const ignored: string[] = [];

  for (const ack of input.acknowledgements) {
    if (new Date(ack.lastSeenAt).getTime() < cutoff) {
      ignored.push(ack.deviceId);
    } else {
      active.push(ack.cursor);
    }
  }

  // No active device, or only stale ones: compact freely. A business nobody is
  // using does not need a change log, and refusing would grow it forever.
  if (active.length === 0) {
    return { compactBelow: input.oldestRetainedCursor, ignoredStaleDeviceIds: ignored };
  }

  // The MINIMUM acknowledged cursor. Tokens are opaque and lexicographically
  // ordered, so the smallest is the one furthest behind.
  const slowest = active.reduce((min, c) => (c < min ? c : min));
  return { compactBelow: slowest, ignoredStaleDeviceIds: ignored };
}

/**
 * Whether a change may be deleted now.
 *
 * `<=`, not `<`, and the off-by-one is the whole subtlety: a device that has
 * acknowledged cursor X has applied everything UP TO AND INCLUDING X, so change
 * X is no longer needed by anyone. Using `<` would pin the newest change of
 * every device forever, and the log would grow without bound.
 */
export function mayCompact(changeCursor: string, decision: CompactionDecision): boolean {
  return changeCursor <= decision.compactBelow;
}

/**
 * A tombstone: a deletion that travels, and can be aged out.
 *
 * WHY A TOMBSTONE AND NOT A DELETE
 * --------------------------------
 * A device that is offline when a customer is deleted still holds that customer.
 * When it reconnects, the first thing it does is push its local state — and the
 * deleted row goes straight back up, resurrected, with no trace of the deletion.
 * The user deletes a customer, closes the app, reopens it, and the customer is
 * back. That is not hypothetical; it is the default outcome of naive
 * last-write-wins deletion.
 *
 * So a DELETE is a row like any other, removed only after every device has seen
 * it.
 */
export interface Tombstone {
  readonly businessId: string;
  readonly entityType: string;
  readonly entityId: string;
  /** When the deletion happened locally. Decides when the tombstone can go. */
  readonly deletedAt: string;
  /** The cursor at which the deletion was published. */
  readonly cursor: string;
  /** The device that deleted it. For the audit trail. */
  readonly deviceId: string;
}

/**
 * Whether a tombstone may finally be dropped.
 *
 * Requires BOTH that every active device has passed the tombstone's cursor AND
 * that it is older than a grace period. The grace period exists because a device
 * that was offline during the deletion may re-push the row, and the tombstone
 * still has to be there to beat it.
 */
export function mayDropTombstone(
  tombstone: Tombstone,
  decision: CompactionDecision,
  now: string,
  graceMs = DEFAULT_STALE_AFTER_MS,
): boolean {
  if (!mayCompact(tombstone.cursor, decision)) return false;
  const age = new Date(now).getTime() - new Date(tombstone.deletedAt).getTime();
  return age >= graceMs;
}

/** What the UI shows, and what the local outbox records. Never a thrown error. */
export type SyncStatusKind =
  /** No provider chosen. The normal state for most users, forever. */
  | 'not_configured'
  /** Connected and current. */
  | 'synced'
  /** Working, or will work later. The app is fully usable. */
  | 'pending'
  /** Something is wrong. The app is still fully usable. */
  | 'error'
  /** Recovery in progress — a long-offline device is fetching a snapshot. */
  | 'recovering'
  /**
   * Sync completed cleanly, but one or more changes require user resolution.
   *
   * This is NOT an error — the sync worked, the data is consistent, and the
   * app is fully usable. The conflicts are real business questions (two devices
   * edited the same financial record), not technical failures. It is a distinct
   * status so the UI can surface it without alarming the user or hiding it
   * behind a green checkmark.
   */
  | 'conflict';

export interface SyncStatus {
  readonly kind: SyncStatusKind;
  readonly businessId: string;
  readonly providerId: string | null;
  readonly deviceId: string;
  /** Mutations in the local outbox not yet accepted by the provider. */
  readonly pendingCount: number;
  /** Mutations that were permanently rejected by the provider and are not retried automatically. */
  readonly failedCount: number;
  readonly lastSuccessfulAt: string | null;
  readonly lastError?: string;
  /** The most recent error message from a failed outbox entry, if any. */
  readonly failedError?: string;
  /** Conflicts awaiting a person. Non-zero means the UI should say so. */
  readonly unresolvedConflictCount: number;
  /** True while a snapshot recovery is in progress. */
  readonly recovering: boolean;
  /** How the last successful sync was triggered. */
  readonly lastSyncOrigin: 'manual' | 'automatic' | 'initial_enable' | null;
  /**
   * Cloud changes this device could not read, from the last download that
   * contained any.
   *
   * A separate field from `lastError` because this is not a failed run: the run
   * succeeded and the device is usable. It is missing DATA, and it is the one
   * condition that would otherwise look exactly like "nothing to do".
   */
  readonly skippedChangeCount?: number;
  readonly skippedChangeReasons?: readonly string[];
  readonly skippedChangeAt?: string;
}
